import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getAuthCookie, setAuthCookie, clearAuthCookies } from '../utils/authCookies';

interface AuthContextType {
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => getAuthCookie('sessionToken'));

  const login = (token: string) => {
    setAuthCookie('sessionToken', token);
    setToken(token);
  };

  const logout = () => {
    clearAuthCookies();
    setToken(null);
  };

  // Re-read cookie when the tab is shown (e.g. logout in another tab cleared cookies).
  useEffect(() => {
    const syncFromCookie = () => {
      if (document.visibilityState !== 'visible') return;
      setToken(getAuthCookie('sessionToken'));
    };
    document.addEventListener('visibilitychange', syncFromCookie);
    return () => document.removeEventListener('visibilitychange', syncFromCookie);
  }, []);

  return (
    <AuthContext.Provider value={{ token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
